package com.macrofocus.eflux.components

import com.macrofocus.common.properties.Property
import com.macrofocus.eflux.data.quest.loadLogicalBranchDataFrames
import com.macrofocus.eflux.network.DerivedBranchColumn
import com.macrofocus.eflux.network.Link
import com.macrofocus.eflux.network.LinkField
import com.macrofocus.eflux.network.physical.Branch
import com.macrofocus.eflux.network.toFixed
import com.macrofocus.eflux.toRawString
import com.macrofocus.eflux.ui.Mark
import com.macrofocus.eflux.ui.PaletteType
import com.macrofocus.eflux.ui.Sparkline
import com.macrofocus.eflux.ui.data.LineButton
import emotion.react.css
import i18next.invoke
import i18next.useTranslation
import js.array.ReadonlyArray
import js.objects.unsafeJso
import kotlinx.coroutines.DelicateCoroutinesApi
import mui.material.Typography
import mui.material.TypographyAlign
import mui.material.styles.Theme
import mui.material.styles.TypographyVariant
import mui.material.styles.useTheme
import mui.system.Box
import mui.system.sx
import org.mkui.react.useProperty
import org.molap.aggregates.cube.DistributiveStatistics
import org.molap.dataframe.DataFrame
import org.molap.dataframe.rowmajor.RowMajorDataFrame
import org.molap.series.Series
import react.*
import react.dom.html.ReactHTML
import web.cssom.*

external interface LinkInfoProps : PropsWithChildren {
    var primary: Boolean
    var link: Link?
    var tooltipLinks: Property<Array<LinkField<*>>>
    var masterDateTime: String?
}

@OptIn(DelicateCoroutinesApi::class)
val LinkInfo = FC<LinkInfoProps> { props ->
    val theme = useTheme<Theme>()
    val context = useRequired(EFluxContext)
    val (t, _) = useTranslation()

    val link = props.link
    val (fullName, _) = useProperty(context.dataLoader.fullName)

    val (displayDateTime, _) = useProperty(context.configuration.displayDateTime)
    val (maxDateTime, _) = useProperty(context.configuration.maxDateTime)
    val (lagUnit, _) = useProperty(context.configuration.lagUnit)
    val paletteType = if (theme.asDynamic().palette.mode == "dark") PaletteType.dark else PaletteType.light

    val (getPathDataFrame, setPathDataFrame) = useState(null as DataFrame<Branch, RowMajorDataFrame.Column<Branch, Any?>, Any?>?)
    useEffect(link, maxDateTime.toRawString()) {
        val logicalBranchId = link?.branch?.logicalBranchId
        if (logicalBranchId != null) {
//                    val branchId = pathSelection[0].branch!!.branch.id
            val date = maxDateTime.toRawString()

            val (_, _, branchDataFrame) = loadLogicalBranchDataFrames(
                logicalBranchId,
                date,
                lagUnit
            )

            setPathDataFrame(branchDataFrame)
        } else {
            setPathDataFrame(null)
        }
    }

    if (link != null) {
        val p = "${link.branch?.p?.amount?.toFixed(0)} ${link.branch?.p?.unit}"
        val pratea = "${(link.branch?.pratea?.times(100.0))?.toFixed(0)}%"

        Box {
            sx {
                display = Display.flex
                justifyContent = JustifyContent.center // centers horizontally
                // alignItems = "center" // use if you also want vertical centering
                width = "1.0".unsafeCast<Width>() // takes full width of parent
                maxWidth = 280.px
                overflowY = Overflow.clip
            }

            LineButton {
                this.dataLoader = context.dataLoader
                this.primary = null
                //            this.probing = if(props.primary) context.dataLoader.controller.secondaryLogicalBranchProbing else context.dataLoader.controller.logicalBranchProbing
                //            this.selection = if(props.primary) context.dataLoader.controller.secondaryLogicalBranchSelection else context.dataLoader.controller.logicalBranchSelection
                this.logicalBranchId = link.id
                this.filled = true
                this.risks = true
            }
        }
//        Tooltip {
//            title = if (!fullName) ReactNode(link.longName) else ReactNode(link.id.name)
//            placement = TooltipPlacement.bottom
//
//            Typography {
//                align = TypographyAlign.center
//                variant = TypographyVariant.subtitle2
//                +if(fullName) link.longName else link.id.name
//            }
//        }

        Box {
            sx {
//                        marginRight = 20.px
                lineHeight = 1.rem
            }

            Typography {
                sx {
                    lineHeight = 1.rem
                }

                align = TypographyAlign.left
                variant = TypographyVariant.body1
//                        asDynamic().display = Display.inline
                ariaLabel = "$pratea @ ${props.masterDateTime}"
                component = ReactHTML.span
                if(link.branch?.p?.amount != null) {
                    +pratea
                } else {
                    +""
                }
            }

            Typography {
                sx {
                    lineHeight = 1.rem
                    float = Float.right
                    marginRight = 10.px
                }

                align = TypographyAlign.right
                variant = TypographyVariant.body1
                ariaLabel = "$p @ ${props.masterDateTime}"
                component = ReactHTML.span

                if(link.branch?.p?.amount != null) {
                    +p
                } else {
                    +""
                }
            }
        }

        Box {
            sx {
//                        marginRight = 20.px
                lineHeight = 1.rem
                display = Display.inlineBlock
                marginBottom = 8.px
            }

            Typography {
                sx {
                    lineHeight = 1.rem
                    verticalAlign = VerticalAlign.top
                }

                align = TypographyAlign.left
                variant = TypographyVariant.caption
//                        asDynamic().display = Display.inline
                component = ReactHTML.span

                +t("variable.link.pratea.label")
            }

            Typography {
                sx {
                    lineHeight = 1.rem
                    float = Float.right
                    marginRight = 10.px
                }

                align = TypographyAlign.right
                variant = TypographyVariant.caption
                component = ReactHTML.span

                +t("variable.link.p.label")
            }
        }

        val dataFrame = getPathDataFrame
        val getWidth = 180.0
        val maximum = 1.0
        val minimum = 0.0

        Box {
            sx {
                display = Display.inlineFlex
                marginLeft = 10.px
            }

            if (dataFrame != null) {

                mui.material.Tooltip {
                    title = ReactNode(t("variable.link.pratea.label"))

                    val valueMarks: ReadonlyArray<Mark> = arrayOf(
                        unsafeJso { value = minimum; label = "0%" },
                        unsafeJso { value = (minimum + maximum) / 2; label = "50%" },
                        unsafeJso { value = maximum; label = "100%" },
                    )

                    ReactHTML.div {
                        css {
                            position = Position.relative
                            height = 100.pct
                            width = 26.px
                        }
                        for (b in valueMarks) {
                            ReactHTML.span {
                                css {
                                    position = Position.absolute
                                    fontSize = 0.875.rem
                                    bottom = ((b.value!!.toDouble() * 100)).pct
                                    transform = "translateY(50%);".unsafeCast<Transform>()
                                }
                                +b.label!!
                            }
                        }
                    }
                }
            }

            val column = DerivedBranchColumn.pratea
            val series = dataFrame?.getColumn(column) as Series<Any?, *>?
            val dateTime = dataFrame?.getColumn(DerivedBranchColumn.datetime) as Series<Any?, *>?
            val statistics = if (series != null) DistributiveStatistics(series) else null
            val dateTimeStatistics = if(dateTime != null) DistributiveStatistics(dateTime) else null

            Sparkline {
                this.series = series
                this.maximum = maximum
                this.minimum = minimum
                this.getWidth = getWidth
                this.dateTime = dateTime
                this.dateTimeStatistics = dateTimeStatistics
                this.statistics = statistics
                this.paletteType = paletteType
                this.displayDateTime = displayDateTime.unixMillisLong
            }
        }
    }
}